import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { TableColumn } from '../types/table';

// Storage key pattern
const getStorageKey = (tableId: string) => `table-prefs-${tableId}`;

// Current schema version for migration
const CURRENT_VERSION = 1;

export interface TablePreferences {
  version: number;
  columnKeys: string[]; // Track which columns existed when saved
  columnWidths: Record<string, number>;
  columnOrder: string[];
  sort: { column: string; direction: 'asc' | 'desc' } | null;
  filters: Record<string, string[]>;
}

export interface UseTablePreferencesOptions<T = any> {
  tableId: string;
  columns: TableColumn<T>[];
  defaultSort?: { column: string; direction: 'asc' | 'desc' };
}

export interface UseTablePreferencesReturn {
  // Column widths
  columnWidths: Record<string, number>;
  setColumnWidth: (columnKey: string, width: number) => void;

  // Column order
  columnOrder: string[];
  setColumnOrder: (order: string[]) => void;

  // Sorting
  sortColumn: string;
  sortDirection: 'asc' | 'desc';
  handleSort: (columnKey: string) => void;

  // Filters
  columnFilters: Record<string, string[]>;
  setColumnFilter: (columnKey: string, values: string[]) => void;
  clearColumnFilter: (columnKey: string) => void;
  clearAllFilters: () => void;

  // Utilities
  resetPreferences: () => void;
}

/**
 * Load preferences from localStorage with schema migration
 */
function loadPreferences(tableId: string, currentColumnKeys: string[]): TablePreferences {
  const defaultPrefs: TablePreferences = {
    version: CURRENT_VERSION,
    columnKeys: currentColumnKeys,
    columnWidths: {},
    columnOrder: currentColumnKeys,
    sort: null,
    filters: {},
  };

  try {
    const stored = localStorage.getItem(getStorageKey(tableId));
    if (!stored) return defaultPrefs;

    const parsed = JSON.parse(stored) as TablePreferences;

    // Migrate preferences if columns have changed
    return migratePreferences(parsed, currentColumnKeys);
  } catch (e) {
    console.warn(`Failed to load table preferences for ${tableId}:`, e);
    return defaultPrefs;
  }
}

/**
 * Migrate preferences when columns change
 * - Removes preferences for columns that no longer exist
 * - Adds new columns to end of column order
 * - Clears sort if sort column was removed
 */
function migratePreferences(
  stored: TablePreferences,
  currentColumnKeys: string[]
): TablePreferences {
  const storedKeys = stored.columnKeys || [];

  // Check if migration is needed
  const keysMatch =
    storedKeys.length === currentColumnKeys.length &&
    storedKeys.every((k, i) => k === currentColumnKeys[i]);

  if (keysMatch) {
    return stored;
  }

  // Find columns that were added
  const newColumns = currentColumnKeys.filter((k) => !storedKeys.includes(k));

  // Migrate column order - keep valid columns in their order, add new ones at end
  const migratedOrder = [
    ...stored.columnOrder.filter((k) => currentColumnKeys.includes(k)),
    ...newColumns,
  ];

  // Migrate column widths - remove widths for removed columns
  const migratedWidths = Object.fromEntries(
    Object.entries(stored.columnWidths).filter(([k]) => currentColumnKeys.includes(k))
  );

  // Migrate filters - remove filters for removed columns
  const migratedFilters = Object.fromEntries(
    Object.entries(stored.filters).filter(([k]) => currentColumnKeys.includes(k))
  );

  // Clear sort if sort column was removed
  const migratedSort =
    stored.sort && currentColumnKeys.includes(stored.sort.column) ? stored.sort : null;

  return {
    version: CURRENT_VERSION,
    columnKeys: currentColumnKeys,
    columnWidths: migratedWidths,
    columnOrder: migratedOrder,
    sort: migratedSort,
    filters: migratedFilters,
  };
}

/**
 * Save preferences to localStorage
 */
function savePreferences(tableId: string, preferences: TablePreferences): void {
  try {
    localStorage.setItem(getStorageKey(tableId), JSON.stringify(preferences));
  } catch (e) {
    console.warn(`Failed to save table preferences for ${tableId}:`, e);
  }
}

/**
 * Hook for managing table preferences with localStorage persistence
 *
 * @example
 * ```tsx
 * const {
 *   columnWidths,
 *   setColumnWidth,
 *   columnOrder,
 *   setColumnOrder,
 *   sortColumn,
 *   sortDirection,
 *   handleSort,
 *   columnFilters,
 *   setColumnFilter,
 * } = useTablePreferences({
 *   tableId: 'shippers-table',
 *   columns,
 *   defaultSort: { column: 'name', direction: 'asc' }
 * });
 * ```
 */
export function useTablePreferences<T = any>({
  tableId,
  columns,
  defaultSort,
}: UseTablePreferencesOptions<T>): UseTablePreferencesReturn {
  const columnKeys = useMemo(() => columns.map((c) => c.key), [columns]);

  // Initialize state from localStorage
  const [preferences, setPreferences] = useState<TablePreferences>(() =>
    loadPreferences(tableId, columnKeys)
  );

  // Track if this is the initial mount to avoid unnecessary saves
  const isInitialMount = useRef(true);

  // Debounce timer ref
  const saveTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Handle column changes (schema migration)
  useEffect(() => {
    const keysMatch =
      preferences.columnKeys.length === columnKeys.length &&
      preferences.columnKeys.every((k, i) => k === columnKeys[i]);

    if (!keysMatch) {
      setPreferences((prev) => migratePreferences(prev, columnKeys));
    }
  }, [columnKeys, preferences.columnKeys]);

  // Persist to localStorage with debouncing
  useEffect(() => {
    // Skip save on initial mount
    if (isInitialMount.current) {
      isInitialMount.current = false;
      return;
    }

    // Clear any pending save
    if (saveTimeoutRef.current) {
      clearTimeout(saveTimeoutRef.current);
    }

    // Debounced save
    saveTimeoutRef.current = setTimeout(() => {
      savePreferences(tableId, preferences);
    }, 100);

    return () => {
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current);
      }
    };
  }, [tableId, preferences]);

  // Column width setter
  const setColumnWidth = useCallback((columnKey: string, width: number) => {
    setPreferences((prev) => ({
      ...prev,
      columnWidths: {
        ...prev.columnWidths,
        [columnKey]: width,
      },
    }));
  }, []);

  // Column order setter
  const setColumnOrder = useCallback((order: string[]) => {
    setPreferences((prev) => ({
      ...prev,
      columnOrder: order,
    }));
  }, []);

  // Sort handler - toggles direction if same column, resets to asc for new column
  const handleSort = useCallback((columnKey: string) => {
    setPreferences((prev) => {
      const currentSort = prev.sort;
      let newSort: TablePreferences['sort'];

      if (currentSort?.column === columnKey) {
        // Toggle direction
        newSort = {
          column: columnKey,
          direction: currentSort.direction === 'asc' ? 'desc' : 'asc',
        };
      } else {
        // New column, start with asc
        newSort = { column: columnKey, direction: 'asc' };
      }

      return { ...prev, sort: newSort };
    });
  }, []);

  // Filter setters
  const setColumnFilter = useCallback((columnKey: string, values: string[]) => {
    setPreferences((prev) => ({
      ...prev,
      filters: {
        ...prev.filters,
        [columnKey]: values,
      },
    }));
  }, []);

  const clearColumnFilter = useCallback((columnKey: string) => {
    setPreferences((prev) => {
      const { [columnKey]: _, ...restFilters } = prev.filters;
      return { ...prev, filters: restFilters };
    });
  }, []);

  const clearAllFilters = useCallback(() => {
    setPreferences((prev) => ({ ...prev, filters: {} }));
  }, []);

  // Reset all preferences
  const resetPreferences = useCallback(() => {
    const defaultPrefs: TablePreferences = {
      version: CURRENT_VERSION,
      columnKeys,
      columnWidths: {},
      columnOrder: columnKeys,
      sort: defaultSort || null,
      filters: {},
    };
    setPreferences(defaultPrefs);
  }, [columnKeys, defaultSort]);

  // Derived sort values
  const sortColumn = preferences.sort?.column || defaultSort?.column || '';
  const sortDirection = preferences.sort?.direction || defaultSort?.direction || 'asc';

  // Ensure column order includes all current columns
  const columnOrder = useMemo(() => {
    const order = preferences.columnOrder;
    // Make sure all current columns are in the order
    const missingCols = columnKeys.filter((k) => !order.includes(k));
    if (missingCols.length > 0) {
      return [...order.filter((k) => columnKeys.includes(k)), ...missingCols];
    }
    return order.filter((k) => columnKeys.includes(k));
  }, [preferences.columnOrder, columnKeys]);

  return {
    columnWidths: preferences.columnWidths,
    setColumnWidth,
    columnOrder,
    setColumnOrder,
    sortColumn,
    sortDirection,
    handleSort,
    columnFilters: preferences.filters,
    setColumnFilter,
    clearColumnFilter,
    clearAllFilters,
    resetPreferences,
  };
}
